import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { customerPaymentAPI, licenceAPI, vehicleAPI } from '../api/index';
import type { CustomerPaymentSummary } from '../types/index';
import { Button } from './Button';

interface PaymentFormProps {
  customerId: string;
  customerName: string;
  summary?: CustomerPaymentSummary;
  onPaymentAdded?: () => void;
  isLoading?: boolean;
  hideAddButton?: boolean;
}

interface PaymentLine {
  id: string;
  serviceItemId: string; // vehicle-{id} or licence-{id}
  serviceItemType: 'vehicle' | 'licence'; // Type of service item
  serviceItemNumber: string; // Vehicle registration or license number (for display)
  serviceType: string; // e.g., "Vehicle Passing", "License Renewal"
  serviceDescription: string; // Optional description
  unitPrice: number;
  itemAmount: number;
  vehicleId?: string;
  licenceId?: string;
}

const SERVICE_TYPES = [
  'Vehicle Passing',
  'License Renewal',
  'Insurance Renewal',
  'PUC Certificate',
  'Fitness Certificate',
  'Road Tax',
  'Environmental Tax',
  'Document Fee',
  'Other',
];

export function PaymentForm({
  customerId,
  customerName,
  summary,
  onPaymentAdded,
  isLoading = false,
}: PaymentFormProps) {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    totalBillAmount: 0,
    paidAmount: 0,
    paymentMethod: 'cash' as 'cash' | 'upi' | 'card' | 'bank_transfer' | 'cheque',
    notes: '',
    items: [] as PaymentLine[],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'service' | 'quick-payment' | 'history'>('service');
  const [expandedPaymentRows, setExpandedPaymentRows] = useState<Set<string>>(new Set());
  const [quickPaymentAmount, setQuickPaymentAmount] = useState('');
  const [quickPaymentMethod, setQuickPaymentMethod] = useState<'cash' | 'upi'>('cash');

  // Fetch vehicles for customer
  const { data: vehiclesData } = useQuery({
    queryKey: ['customer-vehicles', customerId],
    queryFn: async () => {
      const response = await vehicleAPI.listForCustomer(customerId);
      return response.data.vehicles || [];
    },
  });

  // Fetch licenses for customer
  const { data: licencesData } = useQuery({
    queryKey: ['customer-licences', customerId],
    queryFn: async () => {
      const response = await licenceAPI.listForCustomer(customerId);
      return response.data.licences || [];
    },
  });

  // Fetch payment history
  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['customer-payments', customerId],
    queryFn: async () => {
      const response = await customerPaymentAPI.getByCustomer(customerId, 50, 0);
      return response.data || [];
    },
  });

  const togglePaymentRow = (paymentID: string) => {
    const newExpanded = new Set(expandedPaymentRows);
    if (newExpanded.has(paymentID)) {
      newExpanded.delete(paymentID);
    } else {
      newExpanded.add(paymentID);
    }
    setExpandedPaymentRows(newExpanded);
  };

  const buildServiceItemDropdown = () => {
    const options: Array<{
      id: string;
      type: 'vehicle' | 'licence';
      label: string;
      number: string;
      vehicleId?: string;
      licenceId?: string;
    }> = [];

    // Vehicles
    if (vehiclesData && vehiclesData.length > 0) {
      vehiclesData.forEach((vehicle) => {
        options.push({
          id: `vehicle-${vehicle.id}`,
          type: 'vehicle' as const,
          label: `🚗 ${vehicle.registration_number}`,
          number: vehicle.registration_number,
          vehicleId: vehicle.id,
        });
      });
    }

    // Licenses
    if (licencesData && licencesData.length > 0) {
      licencesData.forEach((licence) => {
        options.push({
          id: `licence-${licence.id}`,
          type: 'licence' as const,
          label: `📋 ${licence.licence_number}`,
          number: licence.licence_number,
          licenceId: licence.id,
        });
      });
    }

    return options;
  };

  const handleItemChange = (
    index: number,
    field: keyof PaymentLine,
    value: string | number,
  ) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'unitPrice' ? Number(value) : value,
    };

    // Auto-calculate item amount
    if (field === 'unitPrice' || field === 'serviceType') {
      newItems[index].itemAmount = newItems[index].unitPrice; // qty is always 1
    }

    // Recalculate total
    const totalBill = newItems.reduce((sum, item) => sum + item.itemAmount, 0);

    setFormData((prev) => ({
      ...prev,
      items: newItems,
      totalBillAmount: totalBill,
    }));
  };

  const handleAddItem = () => {
    const newItem: PaymentLine = {
      id: Date.now().toString(),
      serviceItemId: '',
      serviceItemType: 'vehicle',
      serviceItemNumber: '',
      serviceType: '',
      serviceDescription: '',
      unitPrice: 0,
      itemAmount: 0,
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const totalBill = newItems.reduce((sum, item) => sum + item.itemAmount, 0);
    setFormData((prev) => ({
      ...prev,
      items: newItems,
      totalBillAmount: totalBill,
    }));
  };

  const handleServiceItemChange = (index: number, value: string) => {
    const serviceOptions = buildServiceItemDropdown();
    const selected = serviceOptions.find((opt) => opt.id === value);

    if (selected) {
      const newItems = [...formData.items];
      newItems[index] = {
        ...newItems[index],
        serviceItemId: selected.id,
        serviceItemType: selected.type,
        serviceItemNumber: selected.number,
        vehicleId: selected.vehicleId,
        licenceId: selected.licenceId,
      };
      setFormData((prev) => ({
        ...prev,
        items: newItems,
      }));
    }
  };

  const handleSubmitPayment = async () => {
    // Validate service details
    if (formData.items.some((item) => !item.serviceItemId || !item.serviceType || !item.unitPrice)) {
      toast.error('Please fill Service Item, Service Type, and Amount for all rows');
      return;
    }

    if (formData.totalBillAmount === 0) {
      toast.error('Total Bill Amount must be greater than 0');
      return;
    }

    if (formData.paidAmount > formData.totalBillAmount) {
      toast.error('Paid amount cannot exceed total bill amount');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        total_bill_amount: formData.totalBillAmount,
        paid_amount: formData.paidAmount,
        notes: formData.notes,
        items: formData.items.map((item) => ({
          service_type: item.serviceType,
          service_description: item.serviceDescription,
          item_amount: item.itemAmount,
          quantity: 1,
          unit_price: item.unitPrice,
          vehicle_id: item.vehicleId,
          licence_id: item.licenceId,
        })),
        transactions: formData.paidAmount > 0
          ? [
              {
                amount: formData.paidAmount,
                payment_method: formData.paymentMethod,
              },
            ]
          : [],
      };

      await customerPaymentAPI.create(customerId, payload);

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['payment_summary', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-payments', customerId] });

      // Reset form
      setFormData({
        totalBillAmount: 0,
        paidAmount: 0,
        paymentMethod: 'cash',
        notes: '',
        items: [],
      });

      toast.success('Payment recorded successfully!');
      if (onPaymentAdded) {
        onPaymentAdded();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error recording payment: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickPayment = async () => {
    const amount = parseFloat(quickPaymentAmount);

    if (!quickPaymentAmount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setIsSubmitting(true);
    try {
      // For quick payment, record with 0 bill amount and just the payment
      // This way totals aggregate correctly:
      // Total Bill remains same (no new bill)
      // Total Paid increases by this amount
      // Pending = Total Bill - Total Paid
      const payload = {
        total_bill_amount: 0, // No new bill, just payment
        paid_amount: amount,  // Record the payment amount
        notes: `Quick payment entry - ${quickPaymentMethod}`,
        items: [],
        transactions: [
          {
            amount: amount,
            payment_method: quickPaymentMethod,
          },
        ],
      };

      await customerPaymentAPI.create(customerId, payload);

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['payment_summary', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-payments', customerId] });

      // Reset
      setQuickPaymentAmount('');
      setQuickPaymentMethod('cash');

      toast.success('Payment recorded successfully!');
      if (onPaymentAdded) {
        onPaymentAdded();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error recording payment: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pendingAmount = Math.max(0, formData.totalBillAmount - formData.paidAmount);
  const paymentPercentage =
    formData.totalBillAmount > 0
      ? ((formData.paidAmount / formData.totalBillAmount) * 100).toFixed(1)
      : 0;

  const getPaymentStatusLabel = () => {
    if (summary?.payment_status === 'completed') return '✅ Fully Paid';
    if (summary?.payment_status === 'partial') return '⏳ Partially Paid';
    return '⏳ Pending';
  };

  const serviceOptions = buildServiceItemDropdown();

  return (
    <div className="space-y-4">
      {/* Payment Summary Card */}
      {summary && (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-blue-600 font-medium">Total Billed</div>
              <div className="text-2xl font-bold text-blue-900">₹{summary.total_billed.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-green-600 font-medium">Total Paid</div>
              <div className="text-2xl font-bold text-green-900">₹{summary.total_paid.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-red-600 font-medium">Pending Amount</div>
              <div className="text-2xl font-bold text-red-900">₹{summary.pending_amount.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 font-medium">Payment Status</div>
              <div className="text-lg font-semibold text-gray-900">{getPaymentStatusLabel()}</div>
              <div className="text-xs text-gray-600 mt-1">{summary.payment_percentage.toFixed(2)}% Paid</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setExpandedSection('service')}
          className={`px-4 py-3 font-semibold border-b-2 transition-colors ${
            expandedSection === 'service'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          💼 Service Billing
        </button>
        <button
          onClick={() => setExpandedSection('quick-payment')}
          className={`px-4 py-3 font-semibold border-b-2 transition-colors ${
            expandedSection === 'quick-payment'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          💰 Quick Payment
        </button>
      </div>

      {/* Service Billing Section */}
      {expandedSection === 'service' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          {/* Items Table */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">Add Service Charges</h4>
            {serviceOptions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No vehicles or licenses found for this customer
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left p-3 text-gray-600 font-medium">Service Item</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Service Type</th>
                      <th className="text-left p-3 text-gray-600 font-medium">Description (Optional)</th>
                      <th className="text-right p-3 text-gray-600 font-medium">Amount (₹)</th>
                      <th className="text-center p-3 text-gray-600 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {formData.items.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <select
                            value={item.serviceItemId}
                            onChange={(e) => handleServiceItemChange(index, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="">-- Select Item --</option>
                            {serviceOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={item.serviceType}
                            onChange={(e) => handleItemChange(index, 'serviceType', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="">-- Select Type --</option>
                            {SERVICE_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={item.serviceDescription}
                            onChange={(e) => handleItemChange(index, 'serviceDescription', e.target.value)}
                            placeholder="Optional notes..."
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', parseInt(e.target.value) || 0)}
                            placeholder="0"
                            step="1"
                            min="0"
                            className="w-32 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                          />
                        </td>
                        <td className="p-3 text-center">
                          {formData.items.length > 0 && (
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-500 hover:text-red-700 inline-flex"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={handleAddItem}
              disabled={serviceOptions.length === 0}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400"
            >
              <Plus className="w-4 h-4" />
              Add Service Row
            </button>
          </div>

          {/* Summary Cards */}
          {formData.items.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-700 font-medium mb-2">Total Bill Amount</div>
                  <div className="text-3xl font-bold text-blue-900">₹{formData.totalBillAmount.toFixed(2)}</div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <label className="text-sm text-green-700 font-medium mb-2 block">Paid Amount</label>
                  <input
                    type="number"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData((prev) => ({ ...prev, paidAmount: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    step="1"
                    min="0"
                    className="w-full px-3 py-2 border border-green-300 rounded-lg text-2xl font-bold text-green-900 bg-white"
                  />
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="text-sm text-red-700 font-medium mb-2">Pending Amount</div>
                  <div className="text-3xl font-bold text-red-900">₹{pendingAmount.toFixed(2)}</div>
                  <div className="text-xs text-red-600 mt-2">{paymentPercentage}% Paid</div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value as 'cash' | 'upi',
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="cash">💵 Cash</option>
                  <option value="upi">📱 UPI</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this payment..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                  rows={3}
                />
              </div>

              <Button
                onClick={handleSubmitPayment}
                disabled={isSubmitting || isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-semibold"
              >
                {isSubmitting ? 'Saving Payment...' : '💾 Save Service Charges'}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Quick Payment Section */}
      {expandedSection === 'quick-payment' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          <h4 className="text-lg font-semibold text-gray-900">Record Payment for Existing Balance</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Amount (₹)</label>
              <input
                type="number"
                value={quickPaymentAmount}
                onChange={(e) => setQuickPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                step="1"
                min="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-2xl font-bold"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
              <select
                value={quickPaymentMethod}
                onChange={(e) => setQuickPaymentMethod(e.target.value as 'cash' | 'upi')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="cash">💵 Cash</option>
                <option value="upi">📱 UPI</option>
              </select>
            </div>

              <Button
                onClick={handleQuickPayment}
                disabled={isSubmitting || !quickPaymentAmount}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 font-semibold"
              >
                {isSubmitting ? 'Recording Payment...' : '✓ Record Payment'}
              </Button>
          </div>
        </div>
      )}

    </div>
  );
}
